import React from 'react';
import DOMPurify from 'dompurify';
import config from 'config/config';
import muiTheme from '../theme';

// Treating any content with html as unsafe. DOMPurify used to sanitize html to reduce XSS risk.
export const createMarkup = (dirty) => {
    const clean = DOMPurify.sanitize(dirty, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target'],
    });
    return { __html: clean };
};

export const handleFetchError = (response) => {
    if (!response.ok) {
        throw Error(
            `Fetch error status: ${response.status}; statusText: ${response.statusText}`
        );
    }
    return response;
};

export const a11yProps = (index, description) => ({
    id: `${description}-${index}`,
    'aria-controls': `${description}-${index}`,
});

// Method to return a number formatted to one decimal place
export const formatNumber = (value) => {
    if (typeof value !== 'number') {
        return value;
    }
    return (Math.round(value * 10) / 10).toFixed(1);
};

export const generateAnnotations = (data) => {
    // If no data, or the data does not contain information from 2015 and after, do not add the annotation.
    if (
        !data ||
        !data.labels.includes(2015) ||
        data.labels.indexOf(2015) === data.labels.length - 1
    ) {
        return {};
    }

    // Adding a '.5' to the index of '2015' moves the annotation between the '2015' and '2016' labels.
    const indexForAnnotation = Number(`${data.labels.indexOf(2015)}`);

    // The details for this method can be found in the older version of the chartjs-plugin-annotation documentation: https://www.chartjs.org/chartjs-plugin-annotation/1.2.0/samples/line/labelVisibility.html
    const toggleLabel = (ctx, e) => {
        const { chart } = ctx;

        const annotationOpts =
            chart.config.options.plugins.annotation.annotations.line;
        annotationOpts.label.enabled = !annotationOpts.label.enabled;

        // Setting the popup location to the y value of the mouse over event.
        annotationOpts.label.position = `${
            (e.y / ctx.chart.chartArea.height) * 100
        }%`;

        chart.update();
    };

    return {
        line: {
            // This is a non-visible line underneath the 'box' below. In place to trigger the tooltip on hover.
            type: 'line',
            mode: 'vertical',
            borderColor:
                muiTheme.chartPalette.discontinuity.tooltipTriggerBorder,
            borderWidth: 100,
            xMin: indexForAnnotation + 0.5,
            xMax: indexForAnnotation + 0.5,
            label: {
                content: [
                    "Data changed in late 2015. See 'About' for details.",
                ],
                enabled: false,
                textAlign: 'center',
            },
            enter(ctx, e) {
                toggleLabel(ctx, e);
            },
            leave(ctx, e) {
                toggleLabel(ctx, e);
            },
        },
        box: {
            type: 'box',
            backgroundColor: muiTheme.chartPalette.discontinuity.boxBackground,
            borderDash: [6, 6],
            borderWidth: 2,
            xMin: indexForAnnotation,
            xMax: indexForAnnotation + 1,
            drawTime: 'afterDatasetsDraw',
        },
    };
};

/**
 * This object is for generating custom styling objects for use within the 'LegendItems.js' file. We could store the
 * legend style information in the database, but for some cases the style information is included in the FeatureLayer and
 * this object is used to set the legend style and enables us to only store and maintain the style information in one place.
 * Layers that require custom styling are flagged with the parameter 'customStyleTitle'.
 * For example, a value of this parameter could be:  'customLegendStyleNames.eseriSMSCircleFilled.title'.
 *
 * This is to make the subsequent 'styleMethod' accesible from the 'LegendItems.js' file where the
 * 'customStyleTitle' of a layer is used to reference the correct object.  Example:
 *
 * customLegendStyleNames[layer.customStyleTitle].styleMethod()
 *
 * The idea is to have the 'customLegendStyleNames' object grow as we encounter more legend items that
 * require unique styling.  As the object does expand, we want to make sure that we are following esri symbol
 * naming conventions to some degree, attaching a descriptor on the end that explains what kind of style it is we are returning.
 *
 * Example: esriSMSTriangleShaded : {}
 *
 */
export const customLegendStyleNames = {
    esriSMSCircleFilled: {
        title: 'esriSMSCircleFilled',
        styleMethod: function styleMethod(layerStyle, layerOn) {
            const [r, g, b] = layerStyle.color;
            return {
                height: `${layerStyle.size * 2}px`,
                width: `${layerStyle.size * 2}px`,
                borderColor: layerOn
                    ? 'transparent'
                    : muiTheme.palette.grey[300],
                backgroundColor: layerOn
                    ? `rgba(${r},${g},${b}, 1)`
                    : 'transparent',
            };
        },
    },
};

export const formatRows = (layer, stratification, currentTab) => {
    let index = 0;
    const dataArray = [];
    const sortField = layer.customSortField || 'County';

    while (layer && layer.features[index]) {
        const row = { ...layer.features[index] };
        row.id = index;

        if (stratification && currentTab.contentType === 'chart') {
            // Strat data is nested at zero index and needs to be moved to highest level of the object.

            if (row[stratification]) {
                const stratificationArr = Object.entries(
                    row[stratification][0]
                );

                stratificationArr.forEach((item) => {
                    const [key, value] = item;
                    row[key] = value;
                });
            }
        }
        dataArray.push(row);
        index += 1;
    }

    // Sort the array by County field or a custom field if provided
    dataArray.sort((a, b) => {
        if (a[sortField] > b[sortField]) return 1;
        if (a[sortField] <= b[sortField]) return -1;
        return 0;
    });

    return dataArray;
};

export const formatColumns = (columns) => {
    columns.forEach((column) => {
        if (column.customFormat) {
            column.type = 'number';
            // The following `valueFormatter` function, when passed to the column definition, will override the Material
            // UI Data Grid value getter function. Data passed to the XGrid will remain the same internally, but the value
            // displayed is manipulated here -- we convert the missing(-999), less than 0.1(-555) and suppressed(-777) integer
            // values to the strings "Missing*", "<0.1", and "Suppressed*", respectively. For more info, see the Material
            // UI Data Grid documentation here: https://material-ui.com/components/data-grid/columns/#value-getter
            column.valueFormatter = ({ value }) => {
                if (value == null) {
                    // null values are present for stratification data sets - this is in place to avoid errors when using `.toLocaleString()`.
                    return 'N/A';
                }
                if (value === config.missing) {
                    return config.missingText;
                }
                if (value === config.suppressed) {
                    return config.suppressedText;
                }
                if (value === config.lessThanPointOne) {
                    return config.lessThanPointOneText;
                }
                // check if customFormat is set to 2, which indicates the value is a rate:
                if (column.customFormat === 2) {
                    return Number(value).toFixed(1); // ex: 14.3933 --> 14.4 ; 25 --> 25.0
                }

                // If none of the conditions above match (should only be used for count values):
                return value.toLocaleString(); // ex: 21679 --> 21,679
            };
        }
        // Check if we have a function definition object with a "fn" key as part of the columnHeaders
        if (column.fn) {
            createColumnFunction(column);
        }
    });
    return columns;
};

// Method to create a function within a column cell. Required if we want to create a link or other element besides
// plain text within a XDataGrid column cell. Currently renderCell is the only XDataGrid function we are using, but
// other functions can be configured here as needed in the future.
const createColumnFunction = (column) => {
    // In the db we store a JSON object with the function configuration within the fn field
    const { functionName, linkName, params, target, rel } = JSON.parse(
        column.fn
    );

    // The params array holds both the configuration for the parameters and statements for the function to be generated
    const [parameters, statements] = params;

    if (functionName === 'renderCell') {
        // We use the function constructor to create the function from a string, which is safer than eval()
        const functionCreater = {
            /* eslint-disable no-new-func */
            setHref: new Function(parameters, statements),
        };

        // Create the function and element to return on the column
        const renderCellFunction = (param) => {
            const href = functionCreater.setHref(param);
            return (
                <a href={href} target={target} rel={rel}>
                    {linkName}
                </a>
            );
        };

        // Assign the function on the column object
        column[functionName] = renderCellFunction;
    }
    return column;
};

export const formatAttributesWithCommas = (attributes) => {
    // The convention is that all numbers that need to be updated to include commas
    // should include the word 'Format' at the end of the key name:

    Object.entries(attributes).forEach((entry) => {
        if (entry[0].match(/(Format)/)) {
            attributes[entry[0]] = attributes[entry[0]].replace(
                /(\d)(?=(\d{3})+$)/g,
                '$1,'
            );
        }
    });

    return attributes;
};

// Used in the community profile age-sex breakdown chart:
export const determineChartSteps = (max) => {
    // Hard code in chart x-axis steps based on general population size.
    // This is done to eliminate chart.js attempting to change step size automatically
    // which causes chart compression issues on certain view ports.
    switch (true) {
        case max > 10000 && max <= 40000:
            return 5000;
        case max > 40000 && max <= 100000:
            return 15000;
        case max > 100000 && max <= 300000:
            return 50000;
        case max > 300000 && max <= 1000000:
            return 100000;
        case max > 1000000:
            return 1000000;
        default:
            return 0; // Means no custom steps, lets chart.js handle axis steps dynamically (only works well for smaller numbers).
    }
};
